import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent, ANALYTICS_EVENTS } from '../services/analytics';
import { useEffect } from 'react';
import { 
  ArrowRight, 
  Users, 
  Zap, 
  MessageSquare, 
  Target,
  CheckCircle2,
  BarChart3,
  Clock,
  Shield,
  Sparkles,
  ChevronRight,
  Star,
  Rocket,
  Gift,
  Heart,
  XCircle,
  HelpCircle,
  Plus,
  Minus
} from 'lucide-react';

export default function LandingPage() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return null;
  }

  if (user) {
    return null;
  }

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans text-foreground">
      {/* Header */}
      <header className="border-b-2 border-[#161E54] sticky top-0 z-50 bg-white/95 backdrop-blur-sm">
        <div className="container mx-auto px-4 h-18 flex items-center justify-between py-4">
          <div className="flex items-center gap-2 font-black text-2xl text-[#161E54]">
            <div className="bg-[#F16D34] p-1.5 border-2 border-[#161E54] shadow-neo-sm">
              <Target className="w-5 h-5 text-white" />
            </div>
            <span>Retrospace</span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a 
              href="#why-early-access" 
              onClick={(e) => scrollToSection(e, 'why-early-access')}
              className="text-sm font-bold text-[#161E54] hover:text-[#F16D34] transition-colors uppercase tracking-wide"
            >
              Early Access
            </a>
            <a 
              href="#features" 
              onClick={(e) => scrollToSection(e, 'features')}
              className="text-sm font-bold text-[#161E54] hover:text-[#F16D34] transition-colors uppercase tracking-wide"
            >
              Features
            </a>
            <a 
              href="#pricing" 
              onClick={(e) => scrollToSection(e, 'pricing')}
              className="text-sm font-bold text-[#161E54] hover:text-[#F16D34] transition-colors uppercase tracking-wide"
            >
              Pricing
            </a>
            <a 
              href="#how-it-works" 
              onClick={(e) => scrollToSection(e, 'how-it-works')}
              className="text-sm font-bold text-[#161E54] hover:text-[#F16D34] transition-colors uppercase tracking-wide"
            >
              How It Works
            </a>
          </nav>
          <div className="flex items-center gap-4">
            <Link 
              to="/login" 
              className="text-sm font-bold text-[#161E54] hover:text-[#F16D34] transition-colors uppercase tracking-wide hidden sm:block"
            >
              Log in
            </Link>
            <Link 
              to="/register" 
              className="bg-[#161E54] hover:bg-[#F16D34] text-white px-5 py-2.5 text-sm font-bold transition-all shadow-neo hover:shadow-neo-lg hover:-translate-y-0.5 border-2 border-[#161E54] uppercase tracking-wide"
            >
              Register
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-20 md:py-32 relative overflow-hidden bg-[#F7F7F7] border-b-2 border-[#161E54]">
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-[#BBE0EF] border-2 border-[#161E54] text-[#161E54] px-4 py-1.5 text-sm font-bold mb-8 shadow-neo-sm transform -rotate-1 hover:rotate-0 transition-transform">
                <Rocket className="w-4 h-4" />
                <span>Join our Exclusive Early Access Program</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-black tracking-tight text-[#161E54] mb-6 leading-[1.1]">
                Run better retros.
                <span className="block text-[#F16D34] mt-2">Ship better systems.</span>
              </h1>
              
              <p className="text-xl text-[#547792] max-w-2xl mx-auto mb-10 font-medium leading-relaxed">
                Run engaging, productive retrospective sessions that actually drive improvement. Join our Early Access program today and get full premium access for free.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-12">
                <Link 
                  to="/early-access" 
                  onClick={() => trackEvent(ANALYTICS_EVENTS.CLICK_GET_STARTED, { source: 'hero' })}
                  className="w-full sm:w-auto bg-[#F16D34] hover:bg-[#FF986A] text-white px-8 py-4 text-xl font-bold flex items-center justify-center gap-3 transition-all shadow-neo hover:shadow-neo-lg border-2 border-[#161E54] hover:-translate-y-1"
                >
                  Request Early Access
                  <ArrowRight className="w-6 h-6" />
                </Link>
                <Link 
                  to="/login" 
                  onClick={() => trackEvent(ANALYTICS_EVENTS.CLICK_TRY_DEMO)}
                  className="w-full sm:w-auto bg-white hover:bg-[#EAE0CF] border-2 border-[#161E54] text-[#161E54] px-8 py-4 text-xl font-bold transition-all shadow-neo hover:shadow-neo-lg hover:-translate-y-1 flex items-center justify-center"
                >
                  View Live Demo
                </Link>
              </div>
              
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-bold text-[#161E54]">
                <div className="flex items-center gap-2 bg-white px-3 py-1 border border-[#161E54] shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#F16D34]" />
                  <span>Free for Early Adopters</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-3 py-1 border border-[#161E54] shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#F16D34]" />
                  <span>No Credit Card Required</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-3 py-1 border border-[#161E54] shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#F16D34]" />
                  <span>Unlimited Teams & Boards</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-[#FF986A] rounded-full border-4 border-[#161E54] opacity-20 blur-sm animate-float hidden md:block"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-[#BBE0EF] border-4 border-[#161E54] transform rotate-12 opacity-30 hidden md:block"></div>
        </section>

        {/* Video Overview Section */}
        <section className="py-20 bg-white border-b-2 border-[#161E54]">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-4xl md:text-5xl font-black text-[#161E54] mb-6">See Retrospace in Action</h2>
              <p className="text-[#547792] text-xl font-medium">
                Watch a quick overview of how Retrospace helps teams run better retrospectives.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="relative bg-[#161E54] border-4 border-[#161E54] shadow-neo-lg overflow-hidden">
                {/* Video Container with 16:9 aspect ratio */}
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/RYXTRK7cSd0"
                    title="Retrospace Quick Overview"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
              {/* Video caption */}
              <p className="text-center mt-6 text-[#547792] font-medium">
                <span className="bg-[#BBE0EF] px-3 py-1 border-2 border-[#161E54] shadow-neo-sm text-[#161E54] font-bold">
                  Quick Overview • 2 min
                </span>
              </p>
            </div>
          </div>
        </section>

        {/* Brand Bar */}
        <section className="py-10 border-b-2 border-[#161E54] bg-[#161E54] text-white">
          <div className="container mx-auto px-4">
            <p className="text-center text-sm font-medium opacity-80 mb-6 uppercase tracking-widest">Powering agile teams at</p>
            <div className="flex flex-wrap items-center justify-center gap-10 md:gap-20 opacity-90">
              {['Spotify', 'Atlassian', 'Shopify', 'Stripe', 'Notion'].map((company) => (
                <div key={company} className="text-2xl font-black">
                  {company}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-24 bg-[#EAE0CF] border-b-2 border-[#161E54]">
          <div className="container mx-auto px-4">
             <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-[#161E54] mb-6">Stop wasting time in bad meetings</h2>
              <p className="text-[#547792] text-xl font-medium">
                Retrospectives should be the engine of your team's improvement, not a monthly chore.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* The Old Way */}
              <div className="bg-white/50 border-2 border-[#161E54] p-8 relative overflow-hidden grayscale-[0.5]">
                <div className="absolute top-0 left-0 bg-[#94B4C1] text-[#161E54] px-4 py-2 font-black border-b-2 border-r-2 border-[#161E54] text-sm uppercase tracking-wide">
                  The Old Way
                </div>
                <div className="mt-8 space-y-6">
                  <div className="flex items-start gap-4">
                    <XCircle className="w-6 h-6 text-[#161E54] shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#161E54] text-lg">Messy Spreadsheets</h4>
                      <p className="text-[#547792]">Hard to read, impossible to vote on, and specific formatting breaks easily.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <XCircle className="w-6 h-6 text-[#161E54] shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#161E54] text-lg">Quiet Voices Ignored</h4>
                      <p className="text-[#547792]">Loudest person dominates the room. Introverts don't feel safe sharing.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <XCircle className="w-6 h-6 text-[#161E54] shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#161E54] text-lg">Zero Accountability</h4>
                      <p className="text-[#547792]">Action items get written down and immediately forgotten until next month.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* The RetroFlow Way */}
              <div className="bg-white border-2 border-[#161E54] p-8 shadow-neo-lg relative transform md:-translate-y-4">
                 <div className="absolute top-0 left-0 bg-[#F16D34] text-white px-4 py-2 font-black border-b-2 border-r-2 border-[#161E54] text-sm uppercase tracking-wide">
                  The Retrospace Way
                </div>
                 <div className="mt-8 space-y-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-[#F16D34] shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#161E54] text-lg">Visual & Engaging</h4>
                      <p className="text-[#547792]">Drag-and-drop cards, real-time voting, and built-in timers keep energy high.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-[#F16D34] shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#161E54] text-lg">Psychological Safety</h4>
                      <p className="text-[#547792]">Anonymous mode empowers everyone to speak their truth on sensitive topics.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-[#F16D34] shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#161E54] text-lg">Automated Tracking</h4>
                      <p className="text-[#547792]">Action items live in a centralized dashboard and sync with your workflow.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Early Access Section */}
        <section id="why-early-access" className="py-24 bg-white border-b-2 border-[#161E54]">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-[#161E54] mb-6">Why Join Early Access?</h2>
              <p className="text-[#547792] text-xl font-medium">
                Be a pioneer. Help us build the ultimate retrospective tool and get rewarded.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-[#BBE0EF] border-2 border-[#161E54] p-8 shadow-neo hover:shadow-neo-lg transition-all hover:-translate-y-1">
                <div className="bg-white w-16 h-16 border-2 border-[#161E54] flex items-center justify-center mb-6 shadow-neo-sm">
                  <Gift className="w-8 h-8 text-[#161E54]" />
                </div>
                <h3 className="text-2xl font-black text-[#161E54] mb-4">It's 100% Free</h3>
                <p className="text-[#161E54] font-medium leading-relaxed">
                  During our Early Access period, every feature is completely free. No hidden costs, no tiered plans, just full access.
                </p>
              </div>

              <div className="bg-[#FF986A] border-2 border-[#161E54] p-8 shadow-neo hover:shadow-neo-lg transition-all hover:-translate-y-1">
                <div className="bg-white w-16 h-16 border-2 border-[#161E54] flex items-center justify-center mb-6 shadow-neo-sm">
                  <Target className="w-8 h-8 text-[#161E54]" />
                </div>
                <h3 className="text-2xl font-black text-[#161E54] mb-4">Shape the Product</h3>
                <p className="text-[#161E54] font-medium leading-relaxed">
                  Your feedback goes directly to our product roadmap. Request features you need and watch them get built.
                </p>
              </div>

              <div className="bg-[#EAE0CF] border-2 border-[#161E54] p-8 shadow-neo hover:shadow-neo-lg transition-all hover:-translate-y-1">
                <div className="bg-white w-16 h-16 border-2 border-[#161E54] flex items-center justify-center mb-6 shadow-neo-sm">
                  <Heart className="w-8 h-8 text-[#161E54]" />
                </div>
                <h3 className="text-2xl font-black text-[#161E54] mb-4">Grandfathered Pricing</h3>
                <p className="text-[#161E54] font-medium leading-relaxed">
                  Early Access users will receive special perpetual discounts once we launch our paid plans.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-24 bg-[#F7F7F7] border-b-2 border-[#161E54]">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-[#161E54] mb-6">Everything you need</h2>
              <p className="text-[#547792] text-xl font-medium">
                Purpose-built tools to make your team's retrospectives more effective and actionable.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <FeatureCard 
                icon={<MessageSquare className="w-8 h-8" />}
                iconBg="bg-[#BBE0EF]"
                title="Real-time Collaboration"
                description="Share feedback instantly. See updates as they happen with live sync across all participants."
              />
              <FeatureCard 
                icon={<Zap className="w-8 h-8" />}
                iconBg="bg-[#FF986A]"
                title="Action-Oriented"
                description="Turn discussions into concrete action items. Assign owners, set deadlines, and track progress."
              />
              <FeatureCard 
                icon={<Users className="w-8 h-8" />}
                iconBg="bg-[#EAE0CF]"
                title="Team Management"
                description="Organize multiple teams, manage members, and keep all your history in one secure place."
              />
              <FeatureCard 
                icon={<BarChart3 className="w-8 h-8" />}
                iconBg="bg-[#94B4C1]"
                title="Analytics & Insights"
                description="Track trends over time. Identify feedback patterns and measure your team's health."
              />
              <FeatureCard 
                icon={<Clock className="w-8 h-8" />}
                iconBg="bg-[#F16D34] text-white"
                title="Session Timer"
                description="Keep meetings focused with built-in timers. Respect everyone's time."
              />
              <FeatureCard 
                icon={<Shield className="w-8 h-8" />}
                iconBg="bg-[#161E54] text-white"
                title="Anonymous Mode"
                description="Enable anonymous feedback so team members can share openly without fear."
              />
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-24 bg-white border-b-2 border-[#161E54]">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-[#161E54] mb-6">3 Steps to Better Retros</h2>
              <p className="text-[#547792] text-xl font-medium">
                Streamlined process to get your team aligned in under 30 minutes.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto relative">
               {/* Connector Line (Desktop) */}
               <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-1 bg-[#161E54] border-t-2 border-dashed border-gray-300 z-0"></div>

              <StepCard 
                number="1"
                title="Collect"
                description="Team members add cards anonymously. What went well? What didn't?"
              />
              <StepCard 
                number="2"
                title="Vote & Discuss"
                description="Vote on operational topics. Discuss as a team to find root causes."
              />
              <StepCard 
                number="3"
                title="Act"
                description="Create trackable action items. Assign owners and follow up next time."
              />
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="py-24 bg-[#EAE0CF] border-b-2 border-[#161E54]">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-[#161E54] mb-6">Loved by Early Access Teams</h2>
              <p className="text-[#547792] text-xl font-medium">
                See what agile teams are saying about Retrospace.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <TestimonialCard 
                quote="Retrospace transformed our retrospectives from boring meetings into productive sessions."
                author="Sarah Chen"
                role="Engineering Manager"
                company="TechCorp"
              />
              <TestimonialCard 
                quote="The action item tracking is a game-changer. We finally have visibility into what we committed to."
                author="Marcus Johnson"
                role="Scrum Master"
                company="InnovateCo"
              />
              <TestimonialCard 
                quote="Setting up was incredibly easy. We ran our first retro within 10 minutes of signing up."
                author="Emily Rodriguez"
                role="Team Lead"
                company="StartupXYZ"
              />
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-24  bg-[#161E54] border-b-2 border-[#161E54]">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Simple, transparent pricing</h2>
              <p className="text-[#f4f4f4] text-xl font-medium">
                Start for free, upgrade as you grow. No hidden fees.
              </p>
            </div>

      <div className="max-w-4xl mx-auto">
              <div className="bg-white border-2 border-[#161E54] p-8 md:p-12 shadow-neo-lg relative overflow-hidden">
                {/* Badge */}
                <div className="absolute top-0 right-0 bg-[#F16D34] text-white px-6 py-2 font-black uppercase tracking-wider text-sm border-l-2 border-b-2 border-[#161E54]">
                  Limited Time Offer
                </div>

                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div>
                    <h3 className="text-3xl font-black text-[#161E54] mb-4">Early Access Pass</h3>
                    <p className="text-[#547792] text-lg font-medium mb-8">
                      Get full access to all Premium features for free while we build the best retrospective tool together.
                    </p>
                    
                    <div className="flex items-end gap-2 mb-8">
                      <span className="text-6xl font-black text-[#161E54]">Free</span>
                    
                    </div>
                    <div className="bg-gradient-to-br from-amber-50 to-orange-100 border-2 border-[#161E54] p-6 shadow-[4px_4px_0px_#F59E0B] relative overflow-hidden mb-8 group transition-all hover:-translate-y-1">
                      <div className="absolute -top-4 -right-4 opacity-10 transform rotate-12 group-hover:scale-110 transition-transform duration-500">
                        <Gift className="w-24 h-24 text-[#F59E0B]" />
                      </div>
                      <p className="text-amber-900 font-black mb-2 flex items-center gap-2 uppercase tracking-wide text-sm relative z-10">
                        <Gift className="w-5 h-5 text-[#D97706]" />
                        Exclusive Reward
                      </p>
                      <p className="text-amber-950 font-medium leading-relaxed relative z-10">
                        Provide detailed feedback and <span className="font-black bg-[#F59E0B] text-white px-1 ring-2 ring-[#161E54] ring-offset-2 ring-offset-amber-50 ml-1">unlock Premium</span> immediately. Access Smart Context AI & Advanced Analytics for free!
                      </p>
                    </div>

                    <Link 
                     to="/early-access" 
                      onClick={() => trackEvent(ANALYTICS_EVENTS.CLICK_GET_STARTED, { source: 'pricing_card' })}
                      className="inline-flex w-full md:w-auto items-center justify-center gap-2 bg-[#161E54] hover:bg-[#F16D34] text-white px-8 py-4 text-xl font-bold transition-all shadow-neo hover:shadow-neo-lg hover:-translate-y-1 border-2 border-[#161E54]"
                    >
                      Get Started Now
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </div>

                  <div className="space-y-4">
                    <p className="font-bold text-[#161E54] uppercase tracking-wide mb-4">Everything Included:</p>
                    {[
                      "Up to 3 Teams",
                      "20 Members / Team",
                      "Unlimited Retrospectives",
                      "1 Year History Retention",
                      "Jira Integration",
                      "Export used data to CSV/JSON"
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-3 text-[#161E54] font-bold">
                        <div className="bg-[#BBE0EF] p-1 border-2 border-[#161E54] shadow-[2px_2px_0px_#161E54]">
                          <CheckCircle2 className="w-4 h-4" />
                        </div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-24 bg-white border-b-2 border-[#161E54]">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-[#161E54] mb-6">Frequently Asked Questions</h2>
              <p className="text-[#547792] text-xl font-medium">
                Everything you need to know about our Early Access program.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <FAQCard 
                question="Is Early Access free?" 
                answer="Yes! You get access to all Premium features without entering a credit card. Note that fair usage limits apply (e.g., 3 teams, 20 members/team)." 
              />
              <FAQCard 
                question="Will I lose my data when Early Access ends?" 
                answer="No. All your teams, retrospectives, and action items will be preserved. You'll be transitioned to a plan that fits your needs, with a special discount." 
              />
              <FAQCard 
                question="Is my data secure?" 
                answer="Absolutely. We use industry-standard encryption for all data in transit and at rest. We never sell your data to third parties." 
              />
              <FAQCard 
                question="What are the limits?" 
                answer="During Early Access, you can create up to 3 teams and invite 20 members per team. You can create unlimited boards and retrospectives." 
              />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-[#161E54] text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cube-coat.png')] opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-6xl font-black mb-8 leading-tight">
                Ready to fix your retrospectives?
              </h2>
              <p className="text-xl text-[#BBE0EF] mb-12 font-medium">
                Join thousands of teams who have already improved their agile process with Retrospace's Early Access program.
              </p>
              <Link 
                to="/early-access" 
                onClick={() => trackEvent(ANALYTICS_EVENTS.CLICK_GET_STARTED, { source: 'footer_cta' })}
                className="inline-flex items-center gap-3 bg-[#F16D34] hover:bg-[#FF986A] text-white px-10 py-5 text-xl font-bold transition-all shadow-[8px_8px_0px_white] hover:shadow-[4px_4px_0px_white] hover:-translate-y-1 border-2 border-white"
              >
                Join Early Access Now
                <ChevronRight className="w-6 h-6" />
              </Link>
              <p className="mt-8 text-gray-400 text-sm font-medium uppercase tracking-widest">
                No credit card • Free (for now)
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12 bg-white border-t-4 border-[#161E54]">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 font-black text-2xl text-[#161E54] mb-6">
                <div className="bg-[#F16D34] p-1.5 border-2 border-[#161E54] shadow-[2px_2px_0px_#161E54]">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <span>Retrospace</span>
              </div>
              <p className="text-[#547792] font-medium leading-relaxed max-w-sm text-lg">
                The modern, engaging retrospective tool for agile teams. Built to make continuous improvement actually work.
              </p>
            </div>
            
            <div>
              <h4 className="font-black text-[#161E54] mb-6 uppercase tracking-wider border-b-2 border-[#161E54] inline-block pb-1">Product</h4>
              <ul className="space-y-4 text-[#547792] font-bold">
                <li>
                  <a 
                    href="#features" 
                    onClick={(e) => scrollToSection(e, 'features')}
                    className="hover:text-[#F16D34] transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    Features
                  </a>
                </li>
                <li>
                  <a 
                    href="#how-it-works" 
                    onClick={(e) => scrollToSection(e, 'how-it-works')}
                    className="hover:text-[#F16D34] transition-colors flex items-center gap-2 group"
                  >
                   <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    How It Works
                  </a>
                </li>
                <li>
                  <Link to="/early-access" className="hover:text-[#F16D34] transition-colors flex items-center gap-2 group">
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    Early Access
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-black text-[#161E54] mb-6 uppercase tracking-wider border-b-2 border-[#161E54] inline-block pb-1">Legal</h4>
              <ul className="space-y-4 text-[#547792] font-bold">
                <li>
                  <Link to="/privacy" className="hover:text-[#F16D34] transition-colors flex items-center gap-2 group">
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-[#F16D34] transition-colors flex items-center gap-2 group">
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t-2 border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[#161E54] font-bold">
              © {new Date().getFullYear()} Retrospace. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, iconBg, title, description }: { 
  icon: React.ReactNode; 
  iconBg: string;
  title: string; 
  description: string;
}) {
  return (
    <div className="bg-white border-2 border-[#161E54] p-8 shadow-neo hover:shadow-neo-lg transition-all hover:-translate-y-1 group">
      <div className={`mb-6 w-16 h-16 border-2 border-[#161E54] shadow-neo-sm flex items-center justify-center ${iconBg} text-[#161E54]`}>
        {icon}
      </div>
      <h3 className="text-2xl font-bold mb-3 text-[#161E54]">{title}</h3>
      <p className="text-[#547792] font-medium leading-relaxed">
        {description}
      </p>
    </div>
  );
}

function StepCard({ number, title, description }: { number: string; title: string; description: string }) {
  return (
    <div className="text-center relative z-10 bg-white p-4">
      <div className="w-20 h-20 border-2 border-[#161E54] bg-[#F16D34] text-white text-3xl font-black flex items-center justify-center mx-auto mb-6 shadow-neo transform -rotate-3 group-hover:rotate-0 transition-transform">
        {number}
      </div>
      <h3 className="text-2xl font-black mb-3 text-[#161E54]">{title}</h3>
      <p className="text-[#547792] font-medium leading-relaxed">
        {description}
      </p>
    </div>
  );
}

function TestimonialCard({ quote, author, role, company }: { quote: string; author: string; role: string; company: string }) {
  return (
    <div className="bg-white border-2 border-[#161E54] p-8 shadow-neo relative">
      <div className="absolute -top-4 -left-2 text-6xl text-[#EAE0CF] font-serif">"</div>
      <div className="flex gap-1 mb-6 relative z-10">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-5 h-5 fill-[#F16D34] text-[#F16D34] stroke-[#161E54] stroke-1" />
        ))}
      </div>
      <blockquote className="text-[#161E54] mb-8 leading-relaxed font-bold text-lg relative z-10">
        "{quote}"
      </blockquote>
      <div className="border-t-2 border-gray-100 pt-4 relative z-10">
        <p className="font-black text-[#161E54]">{author}</p>
        <p className="text-sm text-[#547792] font-medium">{role} at {company}</p>
      </div>
    </div>
  );
}

function FAQCard({ question, answer }: { question: string; answer: string }) {
  return (
    <div className="bg-white border-2 border-[#161E54] p-6 shadow-[4px_4px_0px_#161E54] transition-all hover:-translate-y-1 hover:shadow-[6px_6px_0px_#161E54]">
      <div className="flex items-start gap-4">
        <div className="bg-[#F16D34] p-1 border-2 border-[#161E54] shrink-0 mt-1">
          <HelpCircle className="w-5 h-5 text-white" />
        </div>
        <div>
          <h4 className="font-black text-[#161E54] text-lg mb-2 leading-tight">{question}</h4>
          <p className="text-[#547792] font-medium leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  );
}


